import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";
import DataGridTable from "components/Table/DataGrid";
import { invoiceGetAllAction } from "actions/invoiceActions";
import { getInvoices } from "selectors/invoiceSelector";
import AddNewInvoice from "./AddNewInvoice";
import FilterMenu from "./FilterMenu";
import "./invoice.scss";
import BurgerMenu from "components/Menu/BurgerMenu";
import {
  CloseOutlined,
  DownloadOutlined,
  EditOutlined,
  PreviewOutlined,
  SearchRounded,
  VisibilityOutlined,
} from "@mui/icons-material";
import { TaskStatus } from "pages/task/addTaskUtils";
import { get } from "lodash";
import { formatNumber, getMomentDate } from "core/utils/localizationUtils";
import DownloadInvoice from "./DownloadInvoice";
import { ModelDialog } from "components/Model";
import PreviewInvoice from "./PreviewInvoice";

const Invoice = ({ propertyId, projectId }) => {
  const dispatch = useDispatch();
  const { invoicesList, totalCount, loading } = useSelector(getInvoices);
  const [pageModel, setPageModel] = useState({ page: 0, pageSize: 10 });
  const [selectedRows, setSelectedRows] = useState([]);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [openAddInvoiceDialog, setOpenAddInvoiceDialog] = useState(false);
  const [editInvoiceData, setEditInvoiceData] = useState(null);
  const [filterState, setFilterState] = useState({
    status: {
      paid: false,
      pending: false,
      draft: false,
    },
    dateRange: {
      from: null,
      to: null,
    },
    amountRange: {
      min: "",
      max: "",
    },
  });

  const [printInvoice, setPrintInvoice] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(false);

  useEffect(() => {
    dispatch(
      invoiceGetAllAction({
        ...pageModel,
        pageNumber: pageModel.page + 1,
        projectId,
        // propertyId,
      })
    );
  }, [dispatch, pageModel, projectId]);

  const handleAddInvoice = () => {
    setEditInvoiceData(null);
    setOpenAddInvoiceDialog(true);
  };

  const handleEditInvoice = (invoiceData) => {
    setEditInvoiceData(invoiceData);
    setOpenAddInvoiceDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenAddInvoiceDialog(false);
    setEditInvoiceData(null);
    dispatch({
      type: "CLEAR_INVOICE_DATA",
    });
  };

  // const handleFilterClick = (event) => {
  //   setFilterAnchorEl(event.currentTarget);
  // };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const columns = [
    {
      field: "invoiceStatus",
      type: "buttons",
      headerName: "",
      width: 100,
      renderCell: ({ row }) => {
        const buttonStatusProps = () => {
          if (row.invoiceStatus === "sent") {
            return {
              variant: "contained",
              color: "primary",
              size: "small",
              className: "invoice-status-button success",
            };
          }
          if (row.invoiceStatus === "draft") {
            return {
              variant: "contained",
              color: "gray",
              size: "small",
              className: "invoice-status-button error",
            };
          }

          return {
            variant: "contained",
            color: "primary",
            size: "small",
            className: "invoice-status-button error",
          };
        };
        return (
          <div className="d-flex ">
            {/* <Button className="me-2" {...buttonStatusProps()}>
              {rowData?.invoiceStatus || "Draft"}
            </Button> */}
            <IconButton
              onClick={() => {
                setEditInvoiceData(row);
                setViewInvoice(true);
              }}
            >
              <VisibilityOutlined
                style={{ fontSize: "1.5rem", color: "#345DAE" }}
              />
            </IconButton>
            <IconButton
              onClick={() => {
                setEditInvoiceData(row);
                setPrintInvoice(true);
              }}
            >
              <DownloadOutlined
                style={{ fontSize: "1.5rem", color: "#345DAE" }}
              />
            </IconButton>
          </div>
        );
      },
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice #",
      width: 150,
    },
    {
      field: "jobName",
      headerName: "Project Stage",
      width: 180,
    },
    {
      field: "invoiceDate",
      headerName: "Invoice Date",
      width: 150,
      valueFormatter: (value) => getMomentDate(value),
    },
    {
      field: "paymentDueDate",
      headerName: "Due Date",
      width: 150,
      valueFormatter: (value) => getMomentDate(value),
    },
    {
      field: "totalAmount",
      headerName: "Invoice Amount",
      width: 100,
      valueFormatter: (value) => {
        return formatNumber(value);
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: ({ row }) => {
        const statusColors = {
          Pending: "#FFBA08",
          Paid: "#36B37E",
          // NotPaid: "#36B37E",
        };
        const priority = get(row, "status", "");
        return (
          <div className="invoice-status-cell">
            <TaskStatus
              status={priority || "Medium"}
              statusColors={statusColors}
            />
          </div>
        );
      },
    },
    {
      field: "billTo",
      headerName: "Homeowner",
      width: 180,
    },
    {
      field: "actions",
      type: "actions",
      flex: 1,
      align: "right",
      getActions: (params) => [
        <BurgerMenu>
          <MenuItem onClick={() => handleEditInvoice(params.row)}>
            <EditOutlined /> <p className="m-0">Edit Invoice</p>
          </MenuItem>
        </BurgerMenu>,
      ],
    },
  ];

  const RenderCustomToolbar = () => {
    return (
      <>
        <div
          className="d-flex justify-content-between mb-2 align-items-center"
          data-testid="documents-list-toolbar"
        >
          <Typography className="total-count-text col-5">
            Displaying {[0].length} of {[0].length}
          </Typography>
          <div className="col-7 d-flex justify-content-end">
            <TextField
              disabled
              variant="outlined"
              className="col-8 form-field me-2 disabled"
              style={{ width: "35%", justifyContent: "end" }}
              placeholder="Search"
              InputProps={{
                startAdornment: <SearchRounded fontSize={"5px"} />,
              }}
            />
            <div
              className="d-flex  me-3 disabled"
              // onClick={handleClick}
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: "#345DAE",
                marginTop: "auto",
                marginBottom: "auto",
                width: "auto",
                justifyContent: "end",
              }}
            >
              <Typography
                variant="body1"
                className="h6 me-2"
                style={{
                  fontSize: "16px",
                  fontFamily: "Barlow",
                  fontWeight: "700",
                }}
              >
                Filters
              </Typography>

              <FilterListIcon style={{ fontSize: "1.5rem" }} />
            </div>
          </div>
        </div>
        <div
          style={{
            height: "1px",
            border: "1px solid #DDDDDD",
            marginBottom: "0.5rem",
          }}
        />
      </>
    );
  };

  if (openAddInvoiceDialog) {
    return (
      <AddNewInvoice
        invoiceId={editInvoiceData?.invoiceId}
        open={openAddInvoiceDialog}
        onClose={handleCloseDialog}
        selectedInvoice={
          editInvoiceData
            ? { invoiceId: editInvoiceData.invoiceId, data: editInvoiceData }
            : {}
        }
        params={{
          projectId,
          // propertyId,
          ...pageModel,
          pageNumber: pageModel.page + 1,
        }}
      />
    );
  }

  return (
    <div className="invoice-container">
      <ModelDialog
        fullWidth={true}
        open={printInvoice || viewInvoice}
        maxWidth="lg"
        dialogTitle={
          viewInvoice ? (
            <div className="d-flex justify-content-end">
              <IconButton>
                <CloseOutlined onClick={() => setViewInvoice(false)} />
              </IconButton>
            </div>
          ) : (
            "Downloading Invoice....."
          )
        }
        dialogContent={
          <DownloadInvoice
            selectedInvoice={editInvoiceData}
            open={printInvoice || viewInvoice}
            type={viewInvoice ? "view" : "download"}
            handleDownloadComplete={() => {
              setPrintInvoice(false);
              setViewInvoice(false);
              setEditInvoiceData(null);
            }}
          />
        }
      />

      <div className="d-flex justify-content-between ">
        <Typography className="h2">Invoices</Typography>
        <div className="invoice-header justify-content-end">
          <button
            className="primaryButton"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddInvoice}
          >
            + Add Invoice
          </button>
        </div>
      </div>

      {/* <div className="invoice-header">
        <Typography variant="h5" component="h1">
          Invoices
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            placeholder="Search Invoice"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <button
            className="outlinedButton"
            startIcon={<FilterListIcon />}
            onClick={handleFilterClick}
          >
            Filter
          </button>

        </Box>
      </div> */}

      <Box sx={{ height: "auto", width: "100%", mt: 2 }}>
        <DataGridTable
          columns={columns}
          data={invoicesList} // Replace with invoicesList when API is connected
          defineRowId={(row) => row.invoiceId}
          pagination={totalCount > 10}
          totalCount={totalCount || invoicesList.length}
          paginationModel={pageModel}
          onPaginationChange={(props) => {
            setPageModel(props);
          }}
          onSelect={(props) => {
            setSelectedRows(props);
          }}
          selectedRows={selectedRows}
          loading={loading}
          showToolbar={false}
          CustomToolbar={RenderCustomToolbar}
          // onRowClick={(params) => handleEditInvoice(params.row)}
          onRowClick={(params, event) => {
            // event.currentTarget is the row container
            // event.target is the actual clicked element

            // Traverse up from event.target to find the cell element with data-field attribute
            let el = event.target;
            while (el && !el.getAttribute?.("data-field")) {
              el = el.parentElement;
            }

            if (el) {
              const clickedField = el.getAttribute("data-field");
              console.log("Check the EL clickedField : ", clickedField);

              const ignoredColumns = ["invoiceStatus", "buttons", "edit"];

              if (ignoredColumns.includes(clickedField)) {
                // Ignore this click on specific column
                event.stopPropagation();
                return;
              }
            }

            // Your row click logic here
            console.log("Row clicked:", params.id);
            handleEditInvoice(params.row);
          }}
        />
      </Box>

      <FilterMenu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        handleClose={handleFilterClose}
        filterState={filterState}
        setFilterState={setFilterState}
      />
    </div>
  );
};

export default Invoice;
